/**
 * Compliance Hub — list page.
 *
 * In-house safety document library: RAMS, Risk Assessments, Method Statements,
 * COSHH and Safety Data Sheets. Admins can create / upload / edit / delete;
 * in-house operatives get a read-only listing they can drill into and grab
 * the PDF for use on site.
 */
import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Plus, UploadSimple, MagnifyingGlass, X, CalendarBlank, Trash, ArrowsClockwise, Warning, CheckCircle, EnvelopeSimple, Folder, FolderPlus, House, CaretRight, FolderOpen, ArrowsOutCardinal, CheckSquare } from "@phosphor-icons/react";

import DateInput from "../components/DateInput";
const ADMIN_ROLES = ["super_admin", "admin", "project_manager"];

const REVIEW_BADGE = {
  overdue: { label: "Review overdue", className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300", Icon: Warning },
  due: { label: "Review due soon", className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", Icon: CalendarBlank },
  ok: { label: "In date", className: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300", Icon: CheckCircle },
  "no-date": { label: "No review date", className: "bg-muted text-muted-foreground", Icon: CalendarBlank },
};

export default function CompliancePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterTag, setFilterTag] = useState("");
  // null = at the root of the current category. When category="all" folder
  // navigation is disabled and this is forced to null.
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);  // [{id, name, category}]
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [moveDoc, setMoveDoc] = useState(null);  // doc currently being moved
  // Bulk-select mode: when active, doc cards show a checkbox, the action bar
  // appears, and clicking a card toggles selection instead of navigating.
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  // Folder navigation is only meaningful inside a single category.
  const folderModeActive = filterCat !== "all";

  // Bump this counter to force a refetch (e.g. after creating/deleting a
  // folder or moving a doc). Kept as state so it slots cleanly into the
  // effect's dependency array.
  const [reloadKey, setReloadKey] = useState(0);
  const refresh = () => setReloadKey(k => k + 1);

  // Bulk select helpers ------------------------------------------------------
  const toggleDocSelected = (docId) => {
    setSelectedDocIds(prev => prev.includes(docId)
      ? prev.filter(x => x !== docId)
      : [...prev, docId]
    );
  };
  const exitBulkMode = () => {
    setBulkSelectMode(false);
    setSelectedDocIds([]);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const docParams = new URLSearchParams();
        if (folderModeActive) docParams.append("category", filterCat);
        const docsUrl = docParams.toString()
          ? `/compliance-hub/documents?${docParams}`
          : "/compliance-hub/documents";

        const folderParams = new URLSearchParams();
        if (folderModeActive) {
          folderParams.append("category", filterCat);
          folderParams.append("parent_folder_id", currentFolderId || "root");
        }
        const foldersUrl = folderModeActive
          ? `/compliance-hub/folders?${folderParams}`
          : null;

        const requests = [
          API.get(docsUrl),
          API.get("/compliance-hub/categories"),
          foldersUrl ? API.get(foldersUrl) : Promise.resolve({ data: { folders: [] } }),
        ];
        if (folderModeActive && currentFolderId) {
          requests.push(API.get(`/compliance-hub/folders/${currentFolderId}`));
        }
        const [docsRes, catsRes, foldersRes, folderDetailRes] = await Promise.all(requests);
        if (cancelled) return;
        setDocs(docsRes.data?.documents || []);
        setCategories(catsRes.data?.categories || []);
        setFolders(foldersRes.data?.folders || []);
        setBreadcrumbs(folderDetailRes?.data?.breadcrumbs || []);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load compliance hub: " + (e.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterCat, currentFolderId, folderModeActive, reloadKey]);

  // Inline handlers
  const onPickCategory = (catId) => {
    setFilterCat(catId);
    setCurrentFolderId(null);
    setBreadcrumbs([]);
  };

  const onEnterFolder = (folder) => {
    setCurrentFolderId(folder.id);
  };

  const onJumpToCrumb = (crumbId) => {
    // Null means jump back to the category root.
    setCurrentFolderId(crumbId || null);
  };

  // Aggregate all tags from all docs for the filter selector
  const allTags = useMemo(() => {
    const set = new Set();
    docs.forEach(d => (d.tags || []).forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [docs]);

  // Filtered, searched list — when in folder mode also restrict to docs that
  // belong to the current folder (null = category root).
  const filteredDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter(d => {
      if (filterCat !== "all" && d.category !== filterCat) return false;
      if (folderModeActive) {
        const docFolder = d.folder_id || null;
        if (docFolder !== currentFolderId) return false;
      }
      if (filterTag && !(d.tags || []).includes(filterTag)) return false;
      if (q) {
        const hay = `${d.title || ""} ${d.notes || ""} ${(d.tags || []).join(" ")} ${d.owner_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [docs, search, filterCat, filterTag, folderModeActive, currentFolderId]);

  // Group by category for the per-category counts shown in chips
  const countsByCat = useMemo(() => {
    const m = { all: docs.length };
    docs.forEach(d => { m[d.category] = (m[d.category] || 0) + 1; });
    return m;
  }, [docs]);

  // Headline stats for the top bar
  const stats = useMemo(() => {
    const overdue = docs.filter(d => d.review_status === "overdue").length;
    const dueSoon = docs.filter(d => d.review_status === "due").length;
    return { total: docs.length, overdue, dueSoon };
  }, [docs]);

  const onDelete = async (doc) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete '${doc.title}' and all its versions?\n\nThis cannot be undone.`)) return;
    try {
      await API.delete(`/compliance-hub/documents/${doc.id}`);
      toast.success("Document deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="compliance-hub-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2 font-[Chivo]">
            <ShieldCheck size={28} weight="duotone" className="text-primary" />
            Compliance
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            In-house RAMS, Risk Assessments, Method Statements, COSHH & Safety Data Sheets
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <DigestButton />
            <Button
              variant={bulkSelectMode ? "default" : "outline"}
              onClick={() => {
                if (bulkSelectMode) exitBulkMode();
                else setBulkSelectMode(true);
              }}
              data-testid="compliance-bulk-select-toggle"
              title={bulkSelectMode ? "Exit bulk select" : "Select multiple to bulk-move"}
            >
              <CheckSquare size={16} className="mr-1" /> {bulkSelectMode ? "Cancel select" : "Select"}
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="compliance-bulk-upload-btn">
              <UploadSimple size={16} className="mr-1" /> Bulk Upload
            </Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="compliance-new-doc-btn">
              <Plus size={16} className="mr-1" /> New Document
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-card border border-border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Total documents</p>
          <p className="text-3xl font-bold mt-1 font-[Chivo]" data-testid="compliance-stat-total">{stats.total}</p>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
          <p className="text-xs text-amber-700 dark:text-amber-300 uppercase tracking-wider">Review due soon (30d)</p>
          <p className="text-3xl font-bold mt-1 font-[Chivo] text-amber-700 dark:text-amber-300" data-testid="compliance-stat-due">{stats.dueSoon}</p>
        </div>
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-4">
          <p className="text-xs text-red-700 dark:text-red-300 uppercase tracking-wider">Review overdue</p>
          <p className="text-3xl font-bold mt-1 font-[Chivo] text-red-700 dark:text-red-300" data-testid="compliance-stat-overdue">{stats.overdue}</p>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search title, notes, tags…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8"
            data-testid="compliance-search"
          />
        </div>
        <Select value={filterTag || "__all__"} onValueChange={v => setFilterTag(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-[200px]" data-testid="compliance-tag-filter"><SelectValue placeholder="All tags" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All tags</SelectItem>
            {allTags.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || filterCat !== "all" || filterTag) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); onPickCategory("all"); setFilterTag(""); }}>
            <X size={14} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => onPickCategory("all")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterCat === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
          data-testid="compliance-cat-all"
        >
          All · <span className="opacity-75">{countsByCat.all || 0}</span>
        </button>
        {categories.map(c => (
          <button
            key={c.id}
            onClick={() => onPickCategory(c.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${filterCat === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
            data-testid={`compliance-cat-${c.id}`}
            title={c.description}
          >
            {c.label} · <span className="opacity-75">{countsByCat[c.id] || 0}</span>
          </button>
        ))}
      </div>

      {/* Breadcrumb + New Folder bar (only when a category is selected) */}
      {folderModeActive && (
        <FolderBreadcrumb
          category={categories.find(c => c.id === filterCat)}
          breadcrumbs={breadcrumbs}
          onJumpToCrumb={onJumpToCrumb}
          isAdmin={isAdmin}
          onNewFolder={() => setCreateFolderOpen(true)}
        />
      )}

      {/* Folder grid (only when inside a category) */}
      {folderModeActive && folders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4" data-testid="compliance-folder-grid">
          {folders.map(f => (
            <FolderCard
              key={f.id}
              folder={f}
              isAdmin={isAdmin}
              onOpen={() => onEnterFolder(f)}
              onDeleted={refresh}
            />
          ))}
        </div>
      )}

      {/* Bulk action bar — appears when user has 1+ docs selected */}
      {bulkSelectMode && (
        <BulkActionBar
          selectedDocs={filteredDocs.filter(d => selectedDocIds.includes(d.id))}
          categories={categories}
          onClear={exitBulkMode}
          onMove={() => setBulkMoveOpen(true)}
          onSelectAll={() => setSelectedDocIds(filteredDocs.map(d => d.id))}
        />
      )}

      {/* Document grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
          Loading documents…
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="text-center py-16 bg-muted/20 border border-dashed border-border rounded-lg">
          <ShieldCheck size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="font-semibold mb-1">No compliance documents</p>
          <p className="text-sm text-muted-foreground mb-4">
            {docs.length === 0
              ? isAdmin ? "Upload your first RAMS, Method Statement or SDS to get started." : "Your admin team hasn't published any compliance documents yet."
              : "Nothing matches the current filters."}
          </p>
          {isAdmin && docs.length === 0 && (
            <Button onClick={() => setBulkOpen(true)}>
              <UploadSimple size={16} className="mr-1" /> Upload PDFs
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map(d => (
            <DocCard
              key={d.id}
              doc={d}
              isAdmin={isAdmin}
              categories={categories}
              bulkSelectMode={bulkSelectMode}
              isSelected={selectedDocIds.includes(d.id)}
              onClick={() => {
                if (bulkSelectMode) toggleDocSelected(d.id);
                else navigate(`/compliance-hub/${d.id}`);
              }}
              onDelete={() => onDelete(d)}
              onMove={() => setMoveDoc(d)}
            />
          ))}
        </div>
      )}

      {/* Create doc dialog */}
      <CreateDocDialog
        key={`create-doc-${createOpen}-${filterCat}-${currentFolderId || "root"}`}
        open={createOpen}
        onOpenChange={setCreateOpen}
        categories={categories}
        defaultCategory={folderModeActive ? filterCat : ""}
        defaultFolderId={folderModeActive ? currentFolderId : null}
        onCreated={(doc) => { setCreateOpen(false); navigate(`/compliance-hub/${doc.id}`); }}
      />
      {/* Bulk upload dialog */}
      <BulkUploadDialog
        key={`bulk-upload-${bulkOpen}-${filterCat}-${currentFolderId || "root"}`}
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        categories={categories}
        defaultCategory={folderModeActive ? filterCat : ""}
        defaultFolderId={folderModeActive ? currentFolderId : null}
        onUploaded={() => { setBulkOpen(false); refresh(); }}
      />
      {/* New folder dialog */}
      <CreateFolderDialog
        key={`create-folder-${createFolderOpen}-${filterCat}-${currentFolderId || "root"}`}
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        category={filterCat}
        parentFolderId={currentFolderId}
        onCreated={() => { setCreateFolderOpen(false); refresh(); }}
      />
      {/* Move doc dialog */}
      <MoveDocDialog
        key={`move-doc-${moveDoc?.id || "none"}`}
        doc={moveDoc}
        open={!!moveDoc}
        onOpenChange={(o) => { if (!o) setMoveDoc(null); }}
        onMoved={() => { setMoveDoc(null); refresh(); }}
      />
      {/* Bulk move dialog */}
      <BulkMoveDialog
        key={`bulk-move-${bulkMoveOpen}-${selectedDocIds.join(",")}`}
        open={bulkMoveOpen}
        onOpenChange={setBulkMoveOpen}
        docs={filteredDocs.filter(d => selectedDocIds.includes(d.id))}
        onMoved={() => { setBulkMoveOpen(false); exitBulkMode(); refresh(); }}
      />
    </div>
  );
}

/* ------------------------- Cards & Dialogs ------------------------- */

function DocCard({ doc, isAdmin, categories, onClick, onDelete, onMove, bulkSelectMode, isSelected }) {
  const cat = categories.find(c => c.id === doc.category);
  const badge = REVIEW_BADGE[doc.review_status || "no-date"] || REVIEW_BADGE["no-date"];
  const Icon = badge.Icon;
  return (
    <div
      className={`bg-card border rounded-lg p-4 hover:shadow-md transition cursor-pointer relative group ${isSelected ? "border-primary ring-2 ring-primary/30" : "border-border"}`}
      onClick={onClick}
      data-testid={`compliance-card-${doc.id}`}
      data-selected={isSelected || undefined}
    >
      {/* Checkbox visible in bulk-select mode */}
      {bulkSelectMode && (
        <div className="absolute top-2 left-2 z-10">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={(e) => { e.stopPropagation(); onClick(); }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-2 cursor-pointer accent-primary"
            data-testid={`compliance-select-${doc.id}`}
          />
        </div>
      )}
      <div className={`flex items-start justify-between gap-2 mb-2 ${bulkSelectMode ? "pl-6" : ""}`}>
        <div className="min-w-0 flex-1">
          <Badge variant="outline" className="text-[10px] mb-1">{cat?.label || doc.category}</Badge>
          <p className="font-semibold leading-tight truncate" title={doc.title}>{doc.title}</p>
        </div>
        {isAdmin && !bulkSelectMode && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            {onMove && (
              <button
                onClick={e => { e.stopPropagation(); onMove(); }}
                className="text-muted-foreground hover:text-foreground p-1 -m-1 rounded"
                data-testid={`compliance-move-${doc.id}`}
                title="Move to folder"
              >
                <ArrowsOutCardinal size={14} />
              </button>
            )}
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="text-muted-foreground hover:text-red-600 p-1 -m-1 rounded"
              data-testid={`compliance-delete-${doc.id}`}
              title="Delete"
            >
              <Trash size={14} />
            </button>
          </div>
        )}
      </div>

      {doc.notes && (
        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{doc.notes}</p>
      )}

      {/* Tags */}
      {(doc.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {(doc.tags || []).slice(0, 4).map(t => (
            <span key={t} className="text-[10px] bg-muted text-muted-foreground rounded px-1.5 py-0.5">#{t}</span>
          ))}
          {(doc.tags || []).length > 4 && (
            <span className="text-[10px] text-muted-foreground">+{doc.tags.length - 4}</span>
          )}
        </div>
      )}

      {/* Meta + review badge */}
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border">
        <span>
          {doc.current_version_number > 0 ? `v${doc.current_version_number}` : "No version"}
          {doc.owner_name ? ` · ${doc.owner_name}` : ""}
        </span>
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium ${badge.className}`}>
          <Icon size={10} weight={doc.review_status === "ok" ? "fill" : "regular"} />
          {doc.next_review_date ? `${(doc.next_review_date || "").slice(0, 10)}` : badge.label}
        </span>
      </div>
    </div>
  );
}

function CreateDocDialog({ open, onOpenChange, categories, defaultCategory, defaultFolderId, onCreated }) {
  // Parent forces a fresh mount each time `open` flips true via the key
  // prop, so initial values pulled from props are always correct without
  // needing a derived-state effect.
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(defaultCategory || "");
  const [tagsRaw, setTagsRaw] = useState("");
  const [notes, setNotes] = useState("");
  const [nextReview, setNextReview] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!category) { toast.error("Pick a category"); return; }
    setSaving(true);
    try {
      const tags = tagsRaw.split(",").map(t => t.trim()).filter(Boolean);
      const payload = {
        title: title.trim(), category, tags, notes,
        next_review_date: nextReview || null,
      };
      // Only attach folder_id when the chosen category matches the
      // pre-filled one (otherwise the folder belongs to a different category
      // and the server would reject it).
      if (defaultFolderId && category === defaultCategory) {
        payload.folder_id = defaultFolderId;
      }
      const res = await API.post("/compliance-hub/documents", payload);
      toast.success("Document created — now upload the PDF");
      onCreated && onCreated(res.data);
    } catch (e) {
      toast.error("Create failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New compliance document</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Roof Access RAMS" data-testid="compliance-create-title" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="compliance-create-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Tags (comma separated)</label>
            <Input value={tagsRaw} onChange={e => setTagsRaw(e.target.value)} placeholder="scaffold, high-rise, asbestos" data-testid="compliance-create-tags" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Next review date (optional)</label>
            <DateInput value={nextReview} onChange={e => setNextReview(e.target.value)} data-testid="compliance-create-review" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Notes (optional)</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Internal notes about this document" data-testid="compliance-create-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="compliance-create-submit">
            {saving ? "Creating…" : "Create & upload PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadDialog({ open, onOpenChange, categories, defaultCategory, defaultFolderId, onUploaded }) {
  // Same pattern as CreateDocDialog — parent remounts via key on open
  // transition, so we can read defaults straight from props at mount.
  const [category, setCategory] = useState(defaultCategory || "");
  const [tagsRaw, setTagsRaw] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!category) { toast.error("Pick a category"); return; }
    if (!files.length) { toast.error("Choose at least one PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("tags", tagsRaw);
      if (defaultFolderId && category === defaultCategory) {
        fd.append("folder_id", defaultFolderId);
      }
      files.forEach(f => fd.append("files", f));
      const res = await API.post("/compliance-hub/documents/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${res.data?.count || files.length} document(s)`);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error("Bulk upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bulk upload PDFs</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Each PDF becomes a new document. The filename (without extension) is used as the title — you can rename them after the upload.
          </p>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Category — applied to every file</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="compliance-bulk-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Tags — applied to every file (comma separated)</label>
            <Input value={tagsRaw} onChange={e => setTagsRaw(e.target.value)} placeholder="scaffold, asbestos" data-testid="compliance-bulk-tags" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">PDF files</label>
            <Input
              type="file"
              multiple
              accept="application/pdf,.pdf"
              onChange={e => setFiles(Array.from(e.target.files || []))}
              data-testid="compliance-bulk-files"
            />
            {files.length > 0 && (
              <ul className="mt-2 text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                {files.map((f, i) => <li key={i}>📄 {f.name} ({(f.size / 1024).toFixed(0)} KB)</li>)}
              </ul>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy || !files.length} data-testid="compliance-bulk-submit">
            {busy ? `Uploading ${files.length}…` : `Upload ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- Folder UI ------------------------- */

/**
 * Breadcrumb row + "New Folder" CTA. Sits above the folder/doc grid when a
 * specific category is selected. Empty crumb path means we're at the
 * category root; clicking the category name jumps back to root from any
 * level. Always renders the "New Folder" button for admins so users have a
 * fast way to organise even at root level.
 */
function FolderBreadcrumb({ category, breadcrumbs, onJumpToCrumb, isAdmin, onNewFolder }) {
  if (!category) return null;
  return (
    <div className="flex items-center justify-between gap-2 mb-3" data-testid="compliance-breadcrumb">
      <nav className="flex items-center gap-1 text-xs flex-wrap min-w-0">
        <button
          onClick={() => onJumpToCrumb(null)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-muted text-foreground/80"
          data-testid="compliance-crumb-root"
        >
          <House size={12} />
          <span className="font-semibold">{category.label}</span>
        </button>
        {breadcrumbs.map((c, idx) => (
          <span key={c.id} className="inline-flex items-center gap-1">
            <CaretRight size={11} className="text-muted-foreground" />
            <button
              onClick={() => onJumpToCrumb(c.id)}
              disabled={idx === breadcrumbs.length - 1}
              className={`px-2 py-1 rounded truncate max-w-[180px] ${idx === breadcrumbs.length - 1 ? "text-foreground font-semibold cursor-default" : "hover:bg-muted text-foreground/80"}`}
              title={c.name}
              data-testid={`compliance-crumb-${c.id}`}
            >
              {c.name}
            </button>
          </span>
        ))}
      </nav>
      {isAdmin && (
        <Button variant="outline" size="sm" onClick={onNewFolder} data-testid="compliance-new-folder-btn">
          <FolderPlus size={14} className="mr-1" /> New Folder
        </Button>
      )}
    </div>
  );
}

/**
 * Folder tile shown alongside doc cards. Clicking the tile drills in.
 * Admin sees a small trash button — server returns 409 if the folder isn't
 * empty so the UI just surfaces the error.
 */
function FolderCard({ folder, isAdmin, onOpen, onDeleted }) {
  const onDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete folder '${folder.name}'?\nThis only works for empty folders — move or delete its contents first if it has any.`)) return;
    try {
      await API.delete(`/compliance-hub/folders/${folder.id}`);
      toast.success("Folder deleted");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error("Delete failed: " + (err.response?.data?.detail || err.message));
    }
  };
  const empty = (folder.doc_count || 0) === 0 && (folder.subfolder_count || 0) === 0;
  return (
    <div
      onClick={onOpen}
      className="bg-card border border-border rounded-lg p-3 hover:shadow-md hover:border-primary/40 transition cursor-pointer group relative"
      data-testid={`compliance-folder-card-${folder.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Folder size={26} weight="duotone" className="text-amber-500 shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold leading-tight truncate" title={folder.name}>{folder.name}</p>
            <p className="text-[10px] text-muted-foreground">
              {folder.doc_count || 0} doc{(folder.doc_count || 0) === 1 ? "" : "s"}
              {(folder.subfolder_count || 0) > 0 ? ` · ${folder.subfolder_count} subfolder${folder.subfolder_count === 1 ? "" : "s"}` : ""}
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={onDelete}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-600 p-1 -m-1 rounded shrink-0"
            data-testid={`compliance-folder-delete-${folder.id}`}
            title={empty ? "Delete folder" : "Folder not empty — move contents first"}
          >
            <Trash size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Dialog for admin to create a new folder. Inherits category + parent
 * folder from the current navigation context. */
function CreateFolderDialog({ open, onOpenChange, category, parentFolderId, onCreated }) {
  // Parent remounts via key — no reset effect required.
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    if (!name.trim()) { toast.error("Folder name is required"); return; }
    setBusy(true);
    try {
      await API.post("/compliance-hub/folders", {
        name: name.trim(),
        category,
        parent_folder_id: parentFolderId || null,
      });
      toast.success("Folder created");
      onCreated && onCreated();
    } catch (e) {
      toast.error("Create failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Folder will be created in <span className="font-semibold text-foreground">{category || "—"}</span>
            {parentFolderId ? <> inside the current folder.</> : <> at the category root.</>}
          </p>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Name</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !busy) onSubmit(); }}
              placeholder="e.g. 2026 Projects"
              autoFocus
              data-testid="compliance-folder-name-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy} data-testid="compliance-folder-create-submit">
            {busy ? "Creating…" : "Create folder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Move a single doc to a different folder within the same category. Lists
 * every folder in the doc's category as a flat list with breadcrumb paths
 * so the user can see nesting at a glance.
 */
function MoveDocDialog({ doc, open, onOpenChange, onMoved }) {
  // Like the other dialogs, the parent remounts this via key — so we can
  // load folders once at mount via a small effect that doesn't depend on
  // changing props (it reads `doc` once at mount time).
  const [allFolders, setAllFolders] = useState([]);
  const [target, setTarget] = useState(doc?.folder_id || "__root__");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!doc) return undefined;
    let cancelled = false;
    setLoading(true);
    // Walk every folder in the doc's category. We only get one level at a
    // time from the backend, so this is a quick BFS — categories normally
    // have well under 100 folders so the total request count is small.
    (async () => {
      try {
        const collected = [];
        const queue = [{ parentId: null, path: [] }];
        const seen = new Set();
        while (queue.length) {
          const { parentId, path } = queue.shift();
          const key = parentId || "__root__";
          if (seen.has(key)) continue;
          seen.add(key);
          const params = new URLSearchParams({ category: doc.category, parent_folder_id: parentId || "root" });
          const { data } = await API.get(`/compliance-hub/folders?${params}`);
          (data?.folders || []).forEach(f => {
            const fullPath = [...path, f.name];
            collected.push({ id: f.id, label: fullPath.join(" / "), depth: fullPath.length });
            queue.push({ parentId: f.id, path: fullPath });
          });
        }
        if (!cancelled) setAllFolders(collected);
      } catch (e) {
        if (!cancelled) toast.error("Could not load folders: " + (e.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // doc is stable for the lifetime of this dialog instance (parent
    // remounts it via key) so a one-shot mount load is the desired behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    if (!doc) return;
    setBusy(true);
    try {
      const folder_id = target === "__root__" ? null : target;
      await API.put(`/compliance-hub/documents/${doc.id}`, { folder_id });
      toast.success("Document moved");
      onMoved && onMoved();
    } catch (e) {
      toast.error("Move failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!doc) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move document</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Move <span className="font-semibold text-foreground">{doc.title}</span> to a different folder within its category.
          </p>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Destination</label>
            <Select value={target} onValueChange={setTarget} disabled={loading || busy}>
              <SelectTrigger data-testid="compliance-move-target"><SelectValue placeholder="Pick a folder" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__root__">
                  <span className="inline-flex items-center gap-2"><House size={12} /> Category root</span>
                </SelectItem>
                {allFolders.map(f => (
                  <SelectItem key={f.id} value={f.id}>
                    <span className="inline-flex items-center gap-2">
                      <FolderOpen size={12} className="text-amber-500" />
                      {f.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loading && <p className="text-[11px] text-muted-foreground mt-1">Loading folders…</p>}
            {!loading && allFolders.length === 0 && (
              <p className="text-[11px] text-muted-foreground mt-1">No folders yet — only the category root is available.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy} data-testid="compliance-move-submit">
            {busy ? "Moving…" : "Move"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- Bulk select ------------------------- */

/**
 * Sticky action bar shown when bulk-select mode is active. Renders:
 *   - Selected count + Select All shortcut
 *   - Mixed-category warning when selected docs span multiple categories
 *   - Move button (opens BulkMoveDialog) — disabled while invariant is violated
 *   - Clear button (exits bulk mode and wipes the selection)
 */
function BulkActionBar({ selectedDocs, categories, onClear, onMove, onSelectAll }) {
  const cats = useMemo(() => Array.from(new Set(selectedDocs.map(d => d.category))).filter(Boolean), [selectedDocs]);
  const mixed = cats.length > 1;
  const sharedCategory = cats.length === 1 ? cats[0] : null;
  const sharedLabel = sharedCategory ? (categories.find(c => c.id === sharedCategory)?.label || sharedCategory) : "";

  return (
    <div
      className="sticky top-2 z-20 bg-primary text-primary-foreground border border-primary rounded-lg shadow-md px-4 py-2 mb-4 flex items-center gap-3 flex-wrap"
      data-testid="compliance-bulk-action-bar"
    >
      <CheckSquare size={18} weight="fill" />
      <span className="font-semibold text-sm" data-testid="compliance-bulk-count">
        {selectedDocs.length} selected
      </span>
      {sharedLabel && (
        <span className="text-[11px] bg-white/15 rounded px-2 py-0.5">
          {sharedLabel}
        </span>
      )}
      {mixed && (
        <span className="inline-flex items-center gap-1 text-[11px] bg-amber-200 text-amber-900 rounded px-2 py-0.5" data-testid="compliance-bulk-mixed-warning">
          <Warning size={11} weight="fill" /> Mixed categories — same category required to bulk-move
        </span>
      )}
      <div className="ml-auto flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSelectAll}
          className="bg-white/10 hover:bg-white/20 text-primary-foreground"
          data-testid="compliance-bulk-select-all"
        >
          Select all visible
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onMove}
          disabled={selectedDocs.length === 0 || mixed}
          data-testid="compliance-bulk-move-btn"
        >
          <ArrowsOutCardinal size={14} className="mr-1" /> Move {selectedDocs.length}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="bg-white/10 hover:bg-white/20 text-primary-foreground"
          data-testid="compliance-bulk-clear"
        >
          <X size={14} className="mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}

/**
 * BulkMoveDialog — picks a destination folder (or "category root") for the
 * selected docs and POSTs to /compliance-hub/documents/bulk-move. Loads
 * every folder in the shared category in a small BFS — categories rarely
 * have hundreds of folders so this is fast and keeps the picker accurate.
 */
function BulkMoveDialog({ docs, open, onOpenChange, onMoved }) {
  const [allFolders, setAllFolders] = useState([]);
  const [target, setTarget] = useState("__root__");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // All docs are guaranteed to share a category by the caller (bulk-move
  // is disabled when mixed). Read it directly.
  const category = docs[0]?.category;

  useEffect(() => {
    if (!category) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const collected = [];
        const queue = [{ parentId: null, path: [] }];
        const seen = new Set();
        while (queue.length) {
          const { parentId, path } = queue.shift();
          const key = parentId || "__root__";
          if (seen.has(key)) continue;
          seen.add(key);
          const params = new URLSearchParams({ category, parent_folder_id: parentId || "root" });
          const { data } = await API.get(`/compliance-hub/folders?${params}`);
          (data?.folders || []).forEach(f => {
            const fullPath = [...path, f.name];
            collected.push({ id: f.id, label: fullPath.join(" / "), depth: fullPath.length });
            queue.push({ parentId: f.id, path: fullPath });
          });
        }
        if (!cancelled) setAllFolders(collected);
      } catch (e) {
        if (!cancelled) toast.error("Could not load folders: " + (e.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Parent remounts via key so a one-shot mount load is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    if (!docs.length) return;
    setBusy(true);
    try {
      const folder_id = target === "__root__" ? null : target;
      const { data } = await API.post("/compliance-hub/documents/bulk-move", {
        doc_ids: docs.map(d => d.id),
        folder_id,
      });
      toast.success(`Moved ${data?.moved || docs.length} document${(data?.moved || docs.length) === 1 ? "" : "s"}`);
      onMoved && onMoved();
    } catch (e) {
      toast.error("Bulk move failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!docs.length) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move {docs.length} document{docs.length === 1 ? "" : "s"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Moving {docs.length} document{docs.length === 1 ? "" : "s"} in the <span className="font-semibold text-foreground">{category || "—"}</span> category.
          </p>
          <ul className="text-[11px] text-muted-foreground max-h-24 overflow-y-auto space-y-0.5 border border-border rounded p-2" data-testid="bulk-move-doc-list">
            {docs.slice(0, 10).map(d => <li key={d.id} className="truncate">📄 {d.title}</li>)}
            {docs.length > 10 && <li className="italic">…and {docs.length - 10} more</li>}
          </ul>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Destination</label>
            <Select value={target} onValueChange={setTarget} disabled={loading || busy}>
              <SelectTrigger data-testid="bulk-move-target"><SelectValue placeholder="Pick a folder" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__root__">
                  <span className="inline-flex items-center gap-2"><House size={12} /> Category root</span>
                </SelectItem>
                {allFolders.map(f => (
                  <SelectItem key={f.id} value={f.id}>
                    <span className="inline-flex items-center gap-2">
                      <FolderOpen size={12} className="text-amber-500" />
                      {f.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {loading && <p className="text-[11px] text-muted-foreground mt-1">Loading folders…</p>}
            {!loading && allFolders.length === 0 && (
              <p className="text-[11px] text-muted-foreground mt-1">No folders yet — only the category root is available.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy} data-testid="bulk-move-submit">
            {busy ? "Moving…" : `Move ${docs.length}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------- Digest button ------------------------- */

/**
 * Header CTA — preview the monthly compliance digest now, or trigger a send
 * to all admins on-demand. Surfaces the same email body the cron sends every
 * 1st of the month at 09:00 UTC.
 */
function DigestButton() {
  const [open, setOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data: payload } = await API.get("/scheduler/compliance-digest-preview");
      setPreviewHtml(payload?.html || "");
      setData(payload?.data || null);
    } catch (e) {
      toast.error("Could not load preview: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const onOpen = async () => {
    setOpen(true);
    if (!previewHtml) await loadPreview();
  };

  const onSend = async () => {
    if (!window.confirm("Send the monthly compliance digest to ALL admins now?")) return;
    setSending(true);
    try {
      const { data: res } = await API.post("/scheduler/send-compliance-digest");
      toast.success(res?.message || "Digest sent");
      setOpen(false);
    } catch (e) {
      toast.error("Send failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="outline" onClick={onOpen} data-testid="compliance-digest-btn">
        <EnvelopeSimple size={16} className="mr-1" /> Monthly Digest
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Monthly Compliance Digest — Preview</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Building preview…</div>
          ) : (
            <>
              {data && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <Stat label="Reviews overdue" value={data.overdue_count} tone="danger" />
                  <Stat label="Due in 30 days" value={data.due_soon_count} tone="warn" />
                  <Stat label="Unacked next 14d" value={data.gap_count} tone="info" />
                </div>
              )}
              <div
                className="border border-border rounded-md p-2 bg-muted/20 max-h-[55vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
                data-testid="compliance-digest-preview"
              />
              <p className="text-[10px] text-muted-foreground mt-2">
                The cron sends this on the 1st of every month at 09:00 UTC to every admin who hasn&apos;t opted out.
              </p>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Close</Button>
            <Button variant="outline" onClick={loadPreview} disabled={loading || sending}>
              <ArrowsClockwise size={14} className="mr-1" /> Refresh
            </Button>
            <Button onClick={onSend} disabled={sending} data-testid="compliance-digest-send">
              <EnvelopeSimple size={14} className="mr-1" /> {sending ? "Sending…" : "Send to all admins now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, tone }) {
  const styles = {
    danger: "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-700 dark:text-red-300",
    warn: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300",
    info: "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300",
  };
  return (
    <div className={`border rounded-md p-2 text-center ${styles[tone] || styles.info}`}>
      <p className="text-[9px] uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-xl font-bold font-[Chivo] mt-0.5">{value}</p>
    </div>
  );
}

